import { useCallback, useEffect, useRef, useState } from 'react';
import { Metronome, type MetronomeBeat } from '@/lib/audio/metronome';
import { getAudioContext } from '@/lib/audio/synth';

interface UseMetronomeOptions {
  initialBpm?: number;
  beatsPerBar?: number;
  onBeat?: (beat: MetronomeBeat) => void;
}

/**
 * Envolve o motor de metrônomo e expõe estado pronto para a interface:
 * BPM, pulsos visuais e início/parada.
 */
export function useMetronome({ initialBpm = 80, beatsPerBar = 4, onBeat }: UseMetronomeOptions = {}) {
  const [bpm, setBpmState] = useState(initialBpm);
  const [isRunning, setIsRunning] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(-1);

  const metronomeRef = useRef<Metronome | null>(null);
  const queueRef = useRef<MetronomeBeat[]>([]);
  const rafRef = useRef<number | null>(null);
  const bpmRef = useRef(initialBpm);
  const beatListenerRef = useRef(onBeat);

  useEffect(() => {
    beatListenerRef.current = onBeat;
  }, [onBeat]);

  const ensureMetronome = useCallback(() => {
    if (!metronomeRef.current) {
      metronomeRef.current = new Metronome({
        bpm: bpmRef.current,
        beatsPerBar,
        accentFirst: true,
        onBeat: (beat) => {
          queueRef.current.push(beat);
          beatListenerRef.current?.(beat);
        },
      });
    }
    return metronomeRef.current;
  }, [beatsPerBar]);

  const loop = useCallback(() => {
    const ctx = getAudioContext();
    if (ctx) {
      const now = ctx.currentTime;
      let latest = -1;
      while (queueRef.current.length > 0 && queueRef.current[0].time <= now) {
        latest = queueRef.current.shift()!.beat;
      }
      if (latest >= 0) setCurrentBeat(latest);
    }
    rafRef.current = window.requestAnimationFrame(loop);
  }, []);

  const start = useCallback(() => {
    const metronome = ensureMetronome();
    metronome.start();
    setIsRunning(true);
    if (rafRef.current === null) {
      rafRef.current = window.requestAnimationFrame(loop);
    }
  }, [ensureMetronome, loop]);

  const stop = useCallback(() => {
    metronomeRef.current?.stop();
    setIsRunning(false);
    setCurrentBeat(-1);
    queueRef.current = [];
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const toggle = useCallback(() => {
    if (isRunning) stop();
    else start();
  }, [isRunning, start, stop]);

  const setBpm = useCallback((value: number) => {
    bpmRef.current = value;
    setBpmState(value);
    metronomeRef.current?.setBpm(value);
  }, []);

  useEffect(() => () => {
    metronomeRef.current?.stop();
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
  }, []);

  return { bpm, setBpm, isRunning, currentBeat, start, stop, toggle, beatCount: beatsPerBar };
}